import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { forgotPassword, googleLogin, login, register, resetPassword, verifyOtp } from '../Redux/Slice/auth.slice';
import { useDispatch, useSelector } from 'react-redux';
import {
    LuEye,
    LuEyeClosed,
    LuGamepad2,
    LuSword,
    LuShield,
    LuZap,
    LuStar,
    LuSparkles,
    LuRocket,
    LuCpu,
    LuAtom,
    LuTarget,
    LuBolt,
    LuGem,
} from "react-icons/lu";
import { useNavigate } from 'react-router-dom';
import { getAllUserNames } from '../Redux/Slice/user.slice';
import google_login from '../Asset/images/google_login.svg';
import { useGoogleLogin } from '@react-oauth/google';
import BackgroundColor from '../component/BackgroundColor';
import SEOHead from '../component/SEOHead';


export default function Login({ setShowLoginModal }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inputRefs = useRef([]);
    const constraintsRef = useRef(null);
    const formConstraintsRef = useRef(null);
    const [email, setEmail] = useState("");
    const [isLogin, setIsLogin] = useState(true);
    const [OtpError, setError] = useState("");
    const [forgotPasswordStep, setForgotPasswordStep] = useState(0);
    const userNames = useSelector((state) => state.user.allNames);
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [isLoading, setIsLoading] = useState(false);
    const [formHasBeenDragged, setFormHasBeenDragged] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const mousePosition = useRef({ x: 0, y: 0 });
    const containerRef = useRef(null);

    useEffect(() => {
        dispatch(getAllUserNames())
    }, [dispatch])



    const formik = useFormik({
        initialValues: {
            userName: '',
            fullName: '',
            email: '',
            password: '',
            showPassword: false,
            resetEmail: '',
            showNewPassword: false,
            showConfirmPassword: false,
            newPassword: '',
            confirmPassword: '',
        },
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: Yup.object({
            userName: !isLogin
                ? Yup.string()
                    .required('User Name Required')
                    .matches(/^\S*$/, 'Username cannot contain spaces')
                    .test('unique-username', 'Username already taken', function (value) {
                        if (!value) return true;
                        const normalizedValue = value.toLowerCase().trim();
                        const allNames = (userNames || []).map(name => name.toLowerCase().trim());
                        return !allNames.includes(normalizedValue);
                    })
                : Yup.string(),

            fullName: !isLogin ? Yup.string().required('Full Name Required') : Yup.string(),

            email: !isLogin ? Yup.string()
                .email('Invalid email address')
                .required('Email is Required') : Yup.string().required('userName / Email Required'),

            password: Yup.string()
                .min(6, "Password must be at least 6 characters")
                .required("Password is required"),

            resetEmail: forgotPasswordStep === 1 && Yup.string()
                .email('Invalid email address')
                .required('Email is required'),

            newPassword: Yup.string()
                .when([], {
                    is: () => forgotPasswordStep === 3,
                    then: (schema) => schema
                        .min(6, "Password must be at least 6 characters")
                        .required("New Password is required"),
                    otherwise: (schema) => schema.notRequired(),
                }),
            confirmPassword: Yup.string()
                .when("newPassword", {
                    is: () => forgotPasswordStep === 3,
                    then: (schema) => schema
                        .oneOf([Yup.ref("newPassword"), null], "Password and Confirm Password not match")
                        .required("Confirm Password is required"),
                    otherwise: (schema) => schema.notRequired(),
                }),
        }),
        onSubmit: async (values) => {
            // console.log('Form submitted with values:', values);
            // console.log('Form validation state:', { isValid: formik.isValid, dirty: formik.dirty, errors: formik.errors });

            setIsLoading(true);
            try {
                if (forgotPasswordStep === 3) {
                    const { newPassword, confirmPassword } = values;
                    handleChangePassword({ newPassword, confirmPassword });
                } else if (forgotPasswordStep === 1) {
                    setEmail(values.resetEmail);
                    const response = await dispatch(forgotPassword({ email: values.resetEmail }));
                    if (response.payload.success) {
                        setForgotPasswordStep(2);
                        // console.log("Password reset email sent.");
                    }
                } else {
                    const handleResponse = (response) => {
                        if (response.payload.success) {
                            // navigate('/');
                            if (response.payload.user && (response.payload.user.role == "admin" || response.payload.user.role == "superadmin")) {
                                sessionStorage.setItem("hasRedirected", "true");
                                navigate("/admin");
                            } else {
                                sessionStorage.setItem("hasRedirected", "true");
                                navigate("/");
                            }
                        }
                    };
                    if (isLogin) {
                        // console.log('Attempting login with:', values);
                        await dispatch(login(values)).then(handleResponse);
                    } else {
                        // console.log('Attempting registration with:', values);
                        await dispatch(register(values)).then(handleResponse);
                    }
                }
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setIsLoading(false);
            }
        },
    });

    // // Debug form state - moved here after formik initialization
    // useEffect(() => {
    //     console.log('Form state changed:', {
    //         isValid: formik.isValid,
    //         dirty: formik.dirty,
    //         values: formik.values,
    //         errors: formik.errors,
    //         touched: formik.touched
    //     });
    // }, [formik.isValid, formik.dirty, formik.values, formik.errors, formik.touched]);

    const isUserNameTaken = () => {
        const entered = formik.values.userName.toLowerCase().trim();
        return userNames.map(name => name.toLowerCase().trim()).includes(entered);
    };

    const handleVerifyOTP = () => {
        setForgotPasswordStep(3);
    };

    const handleChangePassword = (values) => {
        const { newPassword } = values;
        dispatch(resetPassword({ newPassword, email })).then((response) => {
            console.log(response);
            if (response.payload.success) {
                setForgotPasswordStep(0);
            }
        });
    };

    const onComplete = (otpValue) => { };

    const OtphandleChange = (e, index) => {
        const value = e.target.value;
        if (isNaN(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);

        if (value && index < 6 - 1) {
            inputRefs?.current[index + 1]?.focus();
        }

        const otpValue = newOtp.join("");
        if (otpValue.length === 6) {
            onComplete?.(otpValue);
        }
    };

    const OtphandleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs?.current[index - 1]?.focus();
        }
    };

    const OtphandlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);

        if (/^\d+$/.test(pastedData)) {
            const newOtp = [...otp];
            pastedData.split("").forEach((digit, index) => {
                if (index < 6) {
                    newOtp[index] = digit;
                }
            });
            setOtp(newOtp);

            if (pastedData.length === 6) {
                onComplete?.(pastedData);
            }
            const focusIndex = Math.min(pastedData.length, 6 - 1);
            inputRefs?.current[focusIndex]?.focus();
        }
    };

    const OtpSubmit = async (e) => {
        e.preventDefault();
        const otpValue = otp.join("");
        if (otpValue.length !== 6) {
            setError("Please enter the complete OTP.");
            return;
        }
        setError("");
        try {
            const response = await dispatch(
                verifyOtp({ email: email, otp: otpValue })
            );
            console.log(response);
            if (response.payload.success) {
                handleVerifyOTP(otpValue);
            } else {
                setError("OTP verification failed. Please try again.");
            }
        } catch (error) {
            setError("Error verifying OTP. Please try again.");
        }
    };

    const googleLogIn = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: {
                    Authorization: `Bearer ${tokenResponse.access_token}`,
                },
            });
            const userInfo = await res.json();

            const { name, email, sub, picture } = userInfo;
            const formattedUserName = name.replace(/\s+/g, '_');
            console.log("userInfo", userInfo);

            dispatch(googleLogin({ uid: sub, userName: formattedUserName, fullName: name, email, photo: picture })).then((response) => {
                if (response.payload.success) {
                    navigate('/');
                }

                if (response?.payload?.user && (response?.payload?.user?.role === "admin" || response?.payload?.user?.role === "superadmin")) {
                    sessionStorage.setItem('hasRedirected', 'true');
                    navigate("/admin");
                }
            });
        },
    });


    // =============================MouseEvent=========================

    // Track mouse position without causing re-renders
    useEffect(() => {
        const handleMouseMove = (e) => {
            mousePosition.current = { x: e.clientX, y: e.clientY };
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    // Enhanced Floating Icon with Mouse Interaction
    const FloatingIcon = ({
        icon: Icon,
        delay,
        duration = 4,
        className,
        pattern = "space",
        size = 24,
        initialPosition,
        id,
    }) => {
        const elementRef = useRef(null);
        const currentPosition = useRef({ x: 0, y: 0 });
        const isHit = useRef(false);
        const baseMovement = useRef({
            vx: (Math.random() - 0.5) * 0.5, // Base velocity X
            vy: (Math.random() - 0.5) * 0.5, // Base velocity Y
            rotation: Math.random() * 360,
            rotationSpeed: (Math.random() - 0.5) * 2,
        });

        // Pick a random Tailwind color class: "text-primary" or "text-secondary"
        const colorClasses = ["text-primary", "text-secondary"];
        const [iconColorClass] = useState(() => colorClasses[Math.floor(Math.random() * colorClasses.length)]);

        // Initialize random position within container bounds
        useEffect(() => {
            const initializePosition = () => {
                if (elementRef.current && elementRef.current.parentElement) {
                    const parentRect =
                        elementRef.current.parentElement.getBoundingClientRect();
                    const elementRect = elementRef.current.getBoundingClientRect();

                    // Calculate random position within parent bounds
                    const maxX = parentRect.width - elementRect.width;
                    const maxY = parentRect.height - elementRect.height;

                    currentPosition.current = {
                        x: Math.random() * maxX,
                        y: Math.random() * maxY,
                    };

                    // Apply initial position
                    if (elementRef.current) {
                        elementRef.current.style.transform = `translate(${currentPosition.current.x}px, ${currentPosition.current.y}px) rotate(${baseMovement.current.rotation}deg)`;
                    }
                }
            };

            // Initialize position after a short delay to ensure DOM is ready
            const timeoutId = setTimeout(initializePosition, 100);

            return () => clearTimeout(timeoutId);
        }, []);

        // Infinite space movement with mouse interaction
        useEffect(() => {
            let animationId;

            const animate = () => {
                if (!elementRef.current) return;

                const element = elementRef.current;
                const rect = element.getBoundingClientRect();
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;

                // Check mouse collision without causing re-renders
                const mouse = mousePosition.current;
                const distance = Math.sqrt(
                    Math.pow(mouse.x - centerX, 2) + Math.pow(mouse.y - centerY, 2)
                );

                // Mouse interaction
                if (distance < 20 && !isHit.current) {
                    isHit.current = true;

                    // Add push velocity
                    const dirX = centerX - mouse.x;
                    const dirY = centerY - mouse.y;
                    const magnitude = Math.sqrt(dirX * dirX + dirY * dirY) || 1;

                    baseMovement.current.vx += (dirX / magnitude) * 3;
                    baseMovement.current.vy += (dirY / magnitude) * 3;
                    baseMovement.current.rotationSpeed += (Math.random() - 0.5) * 10;

                    // Reset hit after delay
                    setTimeout(() => {
                        isHit.current = false;
                    }, 3000);
                }

                // Continuous space movement
                currentPosition.current.x += baseMovement.current.vx;
                currentPosition.current.y += baseMovement.current.vy;
                baseMovement.current.rotation += baseMovement.current.rotationSpeed;

                // Apply drag/friction gradually
                baseMovement.current.vx *= 0.998;
                baseMovement.current.vy *= 0.998;
                baseMovement.current.rotationSpeed *= 0.995;

                // Boundary reflection (bounce off edges)
                const bounds = element.parentElement?.getBoundingClientRect();
                if (bounds) {
                    const elementWidth = rect.width;
                    const elementHeight = rect.height;
                    const margin = 20; // Keep some margin from edges

                    // Check horizontal boundaries
                    if (
                        currentPosition.current.x + elementWidth / 2 >
                        bounds.width - margin
                    ) {
                        currentPosition.current.x =
                            bounds.width - elementWidth / 2 - margin;
                        baseMovement.current.vx = -Math.abs(baseMovement.current.vx) * 0.8; // Bounce with energy loss
                        baseMovement.current.rotationSpeed += (Math.random() - 0.5) * 5; // Add some spin on bounce
                    } else if (currentPosition.current.x - elementWidth / 2 < -margin) {
                        currentPosition.current.x = elementWidth / 2 - margin;
                        baseMovement.current.vx = Math.abs(baseMovement.current.vx) * 0.8; // Bounce with energy loss
                        baseMovement.current.rotationSpeed += (Math.random() - 0.5) * 5; // Add some spin on bounce
                    }

                    // Check vertical boundaries
                    if (
                        currentPosition.current.y + elementHeight / 2 >
                        bounds.height - margin
                    ) {
                        currentPosition.current.y =
                            bounds.height - elementHeight / 2 - margin;
                        baseMovement.current.vy = -Math.abs(baseMovement.current.vy) * 0.8; // Bounce with energy loss
                        baseMovement.current.rotationSpeed += (Math.random() - 0.5) * 5; // Add some spin on bounce
                    } else if (currentPosition.current.y - elementHeight / 2 < -margin) {
                        currentPosition.current.y = elementHeight / 2 - margin;
                        baseMovement.current.vy = Math.abs(baseMovement.current.vy) * 0.8; // Bounce with energy loss
                        baseMovement.current.rotationSpeed += (Math.random() - 0.5) * 5; // Add some spin on bounce
                    }
                }

                // Apply transform directly to avoid re-renders
                element.style.transform = `translate(${currentPosition.current.x}px, ${currentPosition.current.y}px) rotate(${baseMovement.current.rotation}deg)`;

                animationId = requestAnimationFrame(animate);
            };

            // Start animation with initial delay
            const timeoutId = setTimeout(() => {
                animate();
            }, delay * 1000);

            return () => {
                if (animationId) cancelAnimationFrame(animationId);
                if (timeoutId) clearTimeout(timeoutId);
            };
        }, [delay]);

        return (
            <motion.div
                ref={elementRef}
                className={`absolute z-20 pointer-events-none select-none ${iconColorClass} ${className || ""}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                transition={{ duration: 1, delay: delay }}
            >
                <Icon size={size} />
            </motion.div>
        );
    };

    // Enhanced Space Particle System
    const SpaceParticle = ({ delay, duration, className, type = "star" }) => {
        const elementRef = useRef(null);
        const velocity = useRef({
            x: (Math.random() - 0.5) * 2,
            y: (Math.random() - 0.5) * 2,
            rotation: Math.random() * 360,
            rotationSpeed: (Math.random() - 0.5) * 3,
        });
        const position = useRef({
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
        });

        useEffect(() => {
            let animationId;

            const animate = () => {
                if (!elementRef.current) return;

                const element = elementRef.current;

                // Continuous movement
                position.current.x += velocity.current.x;
                position.current.y += velocity.current.y;
                velocity.current.rotation += velocity.current.rotationSpeed;

                // Boundary reflection (bounce off edges)
                const margin = 10;
                const elementSize = type === "nebula" ? 20 : type === "comet" ? 12 : 2;

                // Check horizontal boundaries
                if (position.current.x + elementSize / 2 > window.innerWidth - margin) {
                    position.current.x = window.innerWidth - elementSize / 2 - margin;
                    velocity.current.x = -Math.abs(velocity.current.x) * 0.9; // Bounce with energy loss
                    velocity.current.rotationSpeed += (Math.random() - 0.5) * 3; // Add some spin on bounce
                } else if (position.current.x - elementSize / 2 < margin) {
                    position.current.x = elementSize / 2 + margin;
                    velocity.current.x = Math.abs(velocity.current.x) * 0.9; // Bounce with energy loss
                    velocity.current.rotationSpeed += (Math.random() - 0.5) * 3; // Add some spin on bounce
                }

                // Check vertical boundaries
                if (
                    position.current.y + elementSize / 2 >
                    window.innerHeight - margin
                ) {
                    position.current.y = window.innerHeight - elementSize / 2 - margin;
                    velocity.current.y = -Math.abs(velocity.current.y) * 0.9; // Bounce with energy loss
                    velocity.current.rotationSpeed += (Math.random() - 0.5) * 3; // Add some spin on bounce
                } else if (position.current.y - elementSize / 2 < margin) {
                    position.current.y = elementSize / 2 + margin;
                    velocity.current.y = Math.abs(velocity.current.y) * 0.9; // Bounce with energy loss
                    velocity.current.rotationSpeed += (Math.random() - 0.5) * 3; // Add some spin on bounce
                }

                // Apply transform directly
                element.style.transform = `translate(${position.current.x}px, ${position.current.y}px) rotate(${velocity.current.rotation}deg)`;

                animationId = requestAnimationFrame(animate);
            };

            const timeoutId = setTimeout(() => {
                animate();
            }, delay * 1000);

            return () => {
                if (animationId) cancelAnimationFrame(animationId);
                if (timeoutId) clearTimeout(timeoutId);
            };
        }, [delay]);

        return (
            <div
                ref={elementRef}
                className={`absolute rounded-full pointer-events-none ${className} ${type === "comet"
                        ? "bg-gradient-to-r from-primary to-purple-500"
                        : type === "nebula"
                            ? "bg-gradient-radial from-primary/20 to-transparent"
                            : "bg-primary/40"
                    }`}
                style={{
                    width: type === "nebula" ? "20px" : type === "comet" ? "3px" : "2px",
                    height:
                        type === "nebula" ? "20px" : type === "comet" ? "12px" : "2px",
                    opacity: 0.6,
                }}
            />
        );
    };

    return (
        <BackgroundColor className='pt-8 md:pt-16 px-4 relative overflow-hidden'>
            <SEOHead
                title="Login"
                description="Sign in to your Yoyo Game account to access your games, orders, and downloads."
                url="https://yoyokhel.com/login"
                noIndex={true}
            />
            {/* Enhanced Particle Background */}
            <div className="absolute inset-0 pointer-events-none">
                {[...Array(100)].map((_, i) => (
                    <div
                        key={`bg-star-${i}`}
                        className="absolute w-px h-px bg-white rounded-full animate-pulse"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${2 + Math.random() * 4}s`,
                        }}
                    />
                ))}
                {/* Moving Particles */}
                {[...Array(30)].map((_, i) => (
                    <SpaceParticle
                        key={`particle-${i}`}
                        delay={i * 0.2}
                        duration={8 + Math.random() * 8}
                        type={i % 5 === 0 ? "comet" : i % 8 === 0 ? "nebula" : "star"}
                    />
                ))}
            </div>

            {/* Enhanced Floating Icons with Mouse Interaction */}
            <div className="absolute inset-0 pointer-events-none">
                <FloatingIcon
                    icon={LuStar}
                    delay={1.5}
                    pattern="space"
                    size={35}
                    id="star"
                />
                <FloatingIcon
                    icon={LuGamepad2}
                    delay={0}
                    pattern="space"
                    size={33}
                    id="gamepad"
                />
                <FloatingIcon
                    icon={LuSword}
                    delay={1}
                    pattern="orbit"
                    size={42}
                    id="sword"
                />
                <FloatingIcon
                    icon={LuShield}
                    delay={1.2}
                    pattern="pulse"
                    size={42}
                    id="shield"
                />
                <FloatingIcon
                    icon={LuStar}
                    delay={1.5}
                    pattern="space"
                    size={35}
                    id="star"
                />
                <FloatingIcon
                    icon={LuGamepad2}
                    delay={0}
                    pattern="space"
                    size={33}
                    id="gamepad"
                />
                <FloatingIcon
                    icon={LuSword}
                    delay={1}
                    pattern="orbit"
                    size={42}
                    id="sword"
                />
                <FloatingIcon
                    icon={LuShield}
                    delay={1.2}
                    pattern="pulse"
                    size={42}
                    id="shield"
                />
                <FloatingIcon
                    icon={LuZap}
                    delay={1.5}
                    pattern="drift"
                    size={34}
                    id="zap"
                />
                <FloatingIcon
                    icon={LuStar}
                    delay={1.5}
                    pattern="space"
                    size={30}
                    id="star"
                />
                <FloatingIcon
                    icon={LuSparkles}
                    delay={2.5}
                    pattern="pulse"
                    size={32}
                    id="sparkles"
                />
                <FloatingIcon
                    icon={LuRocket}
                    delay={2}
                    pattern="space"
                    size={40}
                    id="rocket"
                />
                <FloatingIcon
                    icon={LuCpu}
                    delay={2.7}
                    pattern="orbit"
                    size={34}
                    id="cpu"
                />
                <FloatingIcon
                    icon={LuAtom}
                    delay={3.0}
                    pattern="pulse"
                    size={35}
                    id="atom"
                />
                <FloatingIcon
                    icon={LuTarget}
                    delay={3.7}
                    pattern="drift"
                    size={46}
                    id="target"
                />
                <FloatingIcon
                    icon={LuBolt}
                    delay={6}
                    pattern="space"
                    size={43}
                    id="bolt"
                />
                <FloatingIcon
                    icon={LuGem}
                    delay={5.5}
                    pattern="pulse"
                    size={34}
                    id="gem"
                />
                {/* Add more icons as needed without positioning classes */}
            </div>

            {/* Mouse Trail Effect - Simplified */}
            <div className="fixed inset-0 pointer-events-none z-10">
                <div
                    className="absolute w-3 h-3 bg-primary/30 rounded-full blur-sm transition-all duration-75 ease-out"
                    style={{
                        left: mousePosition.current?.x - 6,
                        top: mousePosition.current?.y - 6,
                        transform: "translate(var(--mouse-x, 0px), var(--mouse-y, 0px))",
                    }}
                />
            </div>


            <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
                {/* Draggable form container */}
                <motion.div

                    className="relative w-full max-w-md select-none mx-auto"
                    style={{ touchAction: "none" }}
                >
                    {/* Enhanced Glowing border effect with color cycling */}
                    <motion.div
                        className="absolute inset-0 rounded-2xl blur-xl opacity-30"
                        animate={{
                            background: [
                                "linear-gradient(45deg, #6366f180 0%, #8b5cf680 50%, #ec489980 100%)",
                                "linear-gradient(45deg, #ec489980 0%, #3b82f680 50%, #6366f180 100%)",
                            ]
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                    />

                    {/* Enhanced glow effect that intensifies on hover/drag */}
                    <motion.div
                        className="absolute inset-0 rounded-2xl blur-2xl opacity-0"
                        whileHover={{ opacity: 0.2 }}
                        whileDrag={{ opacity: 0.4 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            background: "radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, rgba(139, 92, 246, 0.4) 50%, transparent 100%)"
                        }}
                    />

                    <div className="relative z-40 bg-transparent backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl shadow-primary/30 p-6 md:p-8 overflow-hidden">
                        {/* Animated background pattern */}
                        <div className="absolute inset-0 opacity-5">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_#6366f1_1px,_transparent_1px)] bg-[length:20px_20px]" />
                        </div>

                        {/* Header with enhanced gaming theme */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="text-center mb-8 relative z-10"
                        >
                            <div className="flex justify-center mb-4">
                                <motion.div
                                    animate={{
                                        rotate: [0, 10, -10, 0],
                                        scale: [1, 1.1, 1]
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                    className="relative w-16 h-16 bg-gradient-to-br from-primary via-purple-600 to-pink-500 rounded-full flex items-center justify-center shadow-lg shadow-primary/40"
                                >
                                    {/* Inner glow effect */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary/50 to-purple-600/50 rounded-full blur-sm" />
                                    <LuGamepad2 size={32} className="text-white relative z-10" />
                                </motion.div>
                            </div>
                            <motion.h2
                                className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-white to-purple-400 bg-clip-text text-transparent"
                                animate={{
                                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                                }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "linear"
                                }}
                                style={{
                                    backgroundSize: "200% 200%"
                                }}
                            >
                                {isLogin ? 'User Login' : 'Create Account'}
                            </motion.h2>
                            <motion.p
                                className="text-gray-400 text-sm mt-2"
                                animate={{ opacity: [0.7, 1, 0.7] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                {isLogin ? 'Ready to level up?' : 'Join the ultimate gaming community'}
                            </motion.p>
                        </motion.div>

                        <AnimatePresence mode="wait">
                            {forgotPasswordStep === 0 && (
                                <motion.div
                                    key="login-form"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3 }}
                                    className="relative z-10"
                                >
                                    <form className="space-y-4" onSubmit={formik.handleSubmit} onInvalid={(e) => {
                                        console.log('Form invalid event:', e);
                                        e.preventDefault();
                                    }}>
                                        {!isLogin && (
                                            <>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.1 }}
                                                >
                                                    <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                        Full Name
                                                    </label>
                                                    <motion.input
                                                        type="text"
                                                        name="fullName"
                                                        placeholder="Enter your full name"
                                                        onChange={formik.handleChange}
                                                        value={formik.values.fullName}
                                                        whileFocus={{ scale: 1.02 }}
                                                        className="w-full bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400 backdrop-blur-sm hover:bg-gray-800/80"
                                                    />
                                                    {formik.errors.fullName && formik.touched.fullName && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                        >
                                                            <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                            {formik.errors.fullName}
                                                        </motion.div>
                                                    )}
                                                </motion.div>

                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.2 }}
                                                >
                                                    <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                        Username
                                                    </label>
                                                    <motion.input
                                                        type="text"
                                                        name="userName"
                                                        placeholder="Choose a unique username"
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            formik.setFieldValue("userName", value);
                                                        }}
                                                        onBlur={(e) => {
                                                            const trimmed = e.target.value.trim();
                                                            if (/\s/.test(trimmed)) {
                                                                formik.setFieldError('userName', 'Username cannot contain spaces');
                                                            }
                                                            formik.handleBlur(e);
                                                        }}
                                                        value={formik.values.userName}
                                                        whileFocus={{ scale: 1.02 }}
                                                        className="w-full bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400 backdrop-blur-sm hover:bg-gray-800/80"
                                                    />
                                                    {formik.touched.userName && (
                                                        <>
                                                            {formik.errors.userName ? (
                                                                <motion.div
                                                                    initial={{ opacity: 0, y: -10 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                                >
                                                                    <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                                    {formik.errors.userName}
                                                                </motion.div>
                                                            ) : (
                                                                formik.values.userName && (
                                                                    <motion.div
                                                                        initial={{ opacity: 0, y: -10 }}
                                                                        animate={{ opacity: 1, y: 0 }}
                                                                        className={`text-sm mt-1 flex items-center gap-1 ${isUserNameTaken() ? 'text-red-400' : 'text-green-400'}`}
                                                                    >
                                                                        <span className={`w-1 h-1 rounded-full ${isUserNameTaken() ? 'bg-red-400' : 'bg-green-400'}`} />
                                                                        {isUserNameTaken() ? 'Username already taken' : 'Username available'}
                                                                    </motion.div>
                                                                )
                                                            )}
                                                        </>
                                                    )}
                                                </motion.div>
                                            </>
                                        )}

                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 }}
                                        >
                                            <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                {isLogin ? "Email or Username" : "Email"}
                                            </label>
                                            <motion.input
                                                type={isLogin ? "text" : "email"}
                                                name="email"
                                                placeholder={isLogin ? "Enter email or username" : "Enter your email"}
                                                onChange={formik.handleChange}
                                                value={formik.values.email}
                                                whileFocus={{ scale: 1.02 }}
                                                className="w-full bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400 backdrop-blur-sm hover:bg-gray-800/80"
                                            />
                                            {formik.errors.email && formik.touched.email && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                >
                                                    <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                    {formik.errors.email}
                                                </motion.div>
                                            )}
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.4 }}
                                        >
                                            <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                Password
                                            </label>
                                            <div className="relative">
                                                <motion.input
                                                    type={formik.values.showPassword ? "text" : "password"}
                                                    name="password"
                                                    placeholder="Enter your password"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.password}
                                                    whileFocus={{ scale: 1.02 }}
                                                    className="w-full bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400 backdrop-blur-sm hover:bg-gray-800/80"
                                                />
                                                <motion.button
                                                    type="button"
                                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="absolute right-3 top-[15px] text-gray-400 hover:text-white transition-colors"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        formik.setFieldValue(
                                                            "showPassword",
                                                            !formik.values.showPassword
                                                        );
                                                    }}
                                                >
                                                    {formik.values.showPassword ? <LuEye size={20} /> : <LuEyeClosed size={20} />}
                                                </motion.button>
                                            </div>
                                            {formik.errors.password && formik.touched.password && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                >
                                                    <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                    {formik.errors.password}
                                                </motion.div>
                                            )}
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.5 }}
                                            className="text-right"
                                        >
                                            <motion.button
                                                onClick={() => { setForgotPasswordStep(1); }}
                                                type="button"
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                className="text-sm text-primary hover:text-primary/80 transition-colors duration-300 hover:underline"
                                            >
                                                Forgot Password?
                                            </motion.button>
                                        </motion.div>

                                        <motion.button
                                            type="submit"
                                            disabled={isLoading || !formik.isValid || !formik.dirty}
                                            whileHover={{
                                                scale: formik.isValid && formik.dirty ? 1.02 : 1,
                                                boxShadow: formik.isValid && formik.dirty ? "0 0 20px rgba(99, 102, 241, 0.4)" : "none"
                                            }}
                                            whileTap={{ scale: formik.isValid && formik.dirty ? 0.98 : 1 }}
                                            className={`w-full font-bold py-3 rounded-lg transition-all duration-300 relative overflow-hidden ${formik.isValid && formik.dirty
                                                ? 'bg-gradient-to-r from-primary via-purple-600 to-pink-500 text-white hover:shadow-lg hover:shadow-primary/25'
                                                : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
                                                } ${isLoading ? 'opacity-50' : ''}`}
                                            onClick={() => {
                                                if (!formik.isValid) {
                                                    formik.validateForm().then((errors) => {
                                                        console.log('Form validation errors:', errors);
                                                        // Touch all fields to show errors
                                                        formik.setTouched({
                                                            email: true,
                                                            password: true,
                                                            userName: !isLogin,
                                                            fullName: !isLogin
                                                        });
                                                    });
                                                }
                                            }}
                                        >
                                            {/* Animated background */}
                                            {formik.isValid && formik.dirty && (
                                                <motion.div
                                                    className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-primary"
                                                    animate={{
                                                        x: ["-100%", "100%"]
                                                    }}
                                                    transition={{
                                                        duration: 2,
                                                        repeat: Infinity,
                                                        ease: "linear"
                                                    }}
                                                />
                                            )}
                                            <span className="relative z-10">
                                                {isLoading ? (
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mx-auto"
                                                    />
                                                ) : (
                                                    isLogin ? 'Login' : 'Sign Up'
                                                )}
                                            </span>
                                        </motion.button>

                                        {/* Form validation summary */}
                                        {!formik.isValid && formik.submitCount > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="text-red-400 text-sm text-center mt-2 flex items-center justify-center gap-1"
                                            >
                                                <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                Please fix the form errors above
                                            </motion.div>
                                        )}
                                    </form>

                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6 }}
                                        className="mt-6"
                                    >
                                        <div className="flex items-center my-4">
                                            <hr className="flex-grow border border-gray-600/50" />
                                            <span className="px-4 text-gray-400 text-sm">Or continue with</span>
                                            <hr className="flex-grow border border-gray-600/50" />
                                        </div>

                                        <motion.button
                                            onClick={() => { googleLogIn() }}
                                            whileHover={{
                                                scale: 1.02,
                                                backgroundColor: "rgba(55, 65, 81, 0.8)"
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            className="flex items-center justify-center gap-3 bg-primary-dark/20 text-white border border-gray-600/50 w-full py-3 rounded-lg hover:bg-gray-700/60 transition-all duration-300 hover:border-gray-500/50 backdrop-blur-sm"
                                        >
                                            <img
                                                src={google_login}
                                                alt="google_login"
                                                className="w-5 h-5"
                                            />
                                            <span className="font-medium">Continue with Google</span>
                                        </motion.button>

                                        <p className="text-center mt-6 text-sm text-gray-400">
                                            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
                                            <motion.button
                                                onClick={() => setIsLogin(!isLogin)}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                className="text-primary font-medium hover:text-primary/80 transition-colors duration-300 hover:underline"
                                            >
                                                {isLogin ? 'Sign Up' : 'Login'}
                                            </motion.button>
                                        </p>
                                    </motion.div>
                                </motion.div>
                            )}

                            {forgotPasswordStep === 1 && (
                                <motion.div
                                    key="forgot-password"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, y: -20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="text-center mb-6"
                                    >
                                        <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-white to-primary bg-clip-text text-transparent">
                                            Reset Password
                                        </h2>
                                        <p className="text-gray-400 text-sm mt-2">
                                            Enter your email to receive reset instructions
                                        </p>
                                    </motion.div>

                                    <form className="space-y-5" onSubmit={formik.handleSubmit}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 }}
                                        >
                                            <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                Email
                                            </label>
                                            <motion.input
                                                type="email"
                                                name="resetEmail"
                                                placeholder="Enter your email"
                                                onChange={formik.handleChange}
                                                value={formik.values.resetEmail}
                                                whileFocus={{ scale: 1.02 }}
                                                className="w-full bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400 backdrop-blur-sm hover:bg-gray-800/80"
                                            />
                                            {formik.errors.resetEmail && formik.touched.resetEmail && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                >
                                                    <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                    {formik.errors.resetEmail}
                                                </motion.div>
                                            )}
                                        </motion.div>

                                        <motion.button
                                            type="submit"
                                            disabled={isLoading}
                                            whileHover={{
                                                scale: 1.02,
                                                boxShadow: "0 0 20px rgba(99, 102, 241, 0.4)"
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            className="w-full bg-gradient-to-r from-primary via-purple-600 to-pink-500 text-white font-bold py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                                        >
                                            {/* Animated background */}
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-primary"
                                                animate={{
                                                    x: ["-100%", "100%"]
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "linear"
                                                }}
                                            />
                                            <span className="relative z-10">
                                                {isLoading ? (
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mx-auto"
                                                    />
                                                ) : (
                                                    'Send Reset Email'
                                                )}
                                            </span>
                                        </motion.button>
                                    </form>
                                </motion.div>
                            )}

                            {forgotPasswordStep === 2 && (
                                <motion.div
                                    key="otp-verification"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, y: -20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="text-center mb-6"
                                    >
                                        <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-white to-primary bg-clip-text text-transparent">
                                            OTP Verification
                                        </h2>
                                        <p className="text-sm text-gray-400 mt-2">
                                            Enter the 6-digit code sent to <span className="text-primary">{email}</span>
                                        </p>
                                    </motion.div>

                                    <form onSubmit={OtpSubmit}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 }}
                                            className="flex gap-3 justify-center mb-6"
                                        >
                                            {otp.map((digit, index) => (
                                                <motion.input
                                                    key={index}
                                                    type="text"
                                                    value={digit}
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                    maxLength="1"
                                                    ref={(ref) => (inputRefs.current[index] = ref)}
                                                    className="w-12 h-12 text-center bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 text-lg font-bold backdrop-blur-sm hover:bg-gray-800/80"
                                                    onChange={(e) => OtphandleChange(e, index)}
                                                    onKeyDown={(e) => OtphandleKeyDown(e, index)}
                                                    onPaste={OtphandlePaste}
                                                    whileFocus={{ scale: 1.1 }}
                                                />
                                            ))}
                                        </motion.div>

                                        {OtpError && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="text-red-400 text-sm text-center mb-4 flex items-center justify-center gap-1"
                                            >
                                                <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                {OtpError}
                                            </motion.div>
                                        )}

                                        <motion.button
                                            type="submit"
                                            whileHover={{
                                                scale: 1.02,
                                                boxShadow: "0 0 20px rgba(99, 102, 241, 0.4)"
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            className="w-full bg-gradient-to-r from-primary via-purple-600 to-pink-500 text-white font-bold py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 relative overflow-hidden"
                                        >
                                            {/* Animated background */}
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-primary"
                                                animate={{
                                                    x: ["-100%", "100%"]
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "linear"
                                                }}
                                            />
                                            <span className="relative z-10">Verify OTP</span>
                                        </motion.button>
                                    </form>
                                </motion.div>
                            )}

                            {forgotPasswordStep === 3 && (
                                <motion.div
                                    key="reset-password"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, y: -20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="text-center mb-6"
                                    >
                                        <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary via-white to-primary bg-clip-text text-transparent">
                                            Set New Password
                                        </h2>
                                        <p className="text-sm text-gray-400 mt-2">
                                            Create a strong new password for your account
                                        </p>
                                    </motion.div>

                                    <form className="space-y-5" onSubmit={formik.handleSubmit}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.3 }}
                                        >
                                            <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                New Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={formik.values.showNewPassword ? "text" : "password"}
                                                    name="newPassword"
                                                    placeholder="Enter new password"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.newPassword}
                                                    className="w-full bg-gray-800/50 text-white border border-gray-600/50 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400"
                                                />
                                                <motion.button
                                                    type="button"
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        formik.setFieldValue(
                                                            "showNewPassword",
                                                            !formik.values.showNewPassword
                                                        );
                                                    }}
                                                >
                                                    {formik.values.showNewPassword ? <LuEye size={20} /> : <LuEyeClosed size={20} />}
                                                </motion.button>
                                            </div>
                                            {formik.errors.newPassword && formik.touched.newPassword && formik.submitCount > 0 && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                >
                                                    <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                    {formik.errors.newPassword}
                                                </motion.div>
                                            )}
                                        </motion.div>

                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.4 }}
                                        >
                                            <label className="text-sm font-medium text-gray-300 mb-2 block">
                                                Confirm Password
                                            </label>
                                            <div className="relative">
                                                <motion.input
                                                    type={formik.values.showConfirmPassword ? "text" : "password"}
                                                    name="confirmPassword"
                                                    placeholder="Confirm new password"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.confirmPassword}
                                                    whileFocus={{ scale: 1.02 }}
                                                    className="w-full bg-primary-dark/20 text-white border border-gray-600/50 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 placeholder-gray-400 backdrop-blur-sm hover:bg-gray-800/80"
                                                />
                                                <motion.button
                                                    type="button"
                                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        formik.setFieldValue(
                                                            "showConfirmPassword",
                                                            !formik.values.showConfirmPassword
                                                        );
                                                    }}
                                                >
                                                    {formik.values.showConfirmPassword ? <LuEye size={20} /> : <LuEyeClosed size={20} />}
                                                </motion.button>
                                            </div>
                                            {formik.errors.confirmPassword && formik.touched.confirmPassword && formik.submitCount > 0 && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className="text-red-400 text-sm mt-1 flex items-center gap-1"
                                                >
                                                    <span className="w-1 h-1 bg-red-400 rounded-full" />
                                                    {formik.errors.confirmPassword}
                                                </motion.div>
                                            )}
                                        </motion.div>

                                        <motion.button
                                            type="submit"
                                            disabled={isLoading}
                                            whileHover={{
                                                scale: 1.02,
                                                boxShadow: "0 0 20px rgba(99, 102, 241, 0.4)"
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            className="w-full bg-gradient-to-r from-primary via-purple-600 to-pink-500 text-white font-bold py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                                        >
                                            {/* Animated background */}
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-primary"
                                                animate={{
                                                    x: ["-100%", "100%"]
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: Infinity,
                                                    ease: "linear"
                                                }}
                                            />
                                            <span className="relative z-10">
                                                {isLoading ? (
                                                    <motion.div
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full mx-auto"
                                                    />
                                                ) : (
                                                    'Reset Password'
                                                )}
                                            </span>
                                        </motion.button>
                                    </form>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>

            {/* Mouse trail effect */}
            <div className="fixed inset-0 pointer-events-none z-0">
                {[...Array(5)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-primary/20 rounded-full"
                        animate={{
                            x: [0, Math.random() * window.innerWidth],
                            y: [0, Math.random() * window.innerHeight],
                            opacity: [0, 1, 0],
                            scale: [0, 1, 0],
                        }}
                        transition={{
                            duration: 8 + i * 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 1.5
                        }}
                    />
                ))}
            </div>
            {/* Floating Mouse Interaction Hint */}
            <motion.div
                className="fixed bottom-6 right-6 bg-gray-800/80 text-white px-4 py-2 rounded-lg text-sm backdrop-blur-sm border border-gray-600/50 z-40"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2, duration: 0.5 }}
            >
                <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex items-center gap-2"
                >
                    <LuTarget size={16} className="text-primary" />
                    <span>Hover over elements!</span>
                </motion.div>
            </motion.div>
        </BackgroundColor>
    )
}
